import { useRef, useState } from 'react';
import { BookOpen, ChevronDown, Lightbulb, Info, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { FadeIn } from '@/components/motion';
import { cn } from '@/lib/utils/cn';
import { ChapterSidebar } from './chapter-sidebar';
import { manualSections } from '../data/manual-chapters';
import { chapterIllustrations } from './chapter-illustrations';

/* ─── Callout renderer ───────────────────────────────────────── */
function Callout({ type, text }: { type: 'tip' | 'nota' | 'importante'; text: string }) {
  const config = {
    tip: {
      icon: Lightbulb,
      bg: 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800',
      iconColor: 'text-green-600',
      title: 'Consejo',
      titleColor: 'text-green-800 dark:text-green-300',
      textColor: 'text-green-700 dark:text-green-400',
    },
    nota: {
      icon: Info,
      bg: 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800',
      iconColor: 'text-blue-600',
      title: 'Nota',
      titleColor: 'text-blue-800 dark:text-blue-300',
      textColor: 'text-blue-700 dark:text-blue-400',
    },
    importante: {
      icon: AlertTriangle,
      bg: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800',
      iconColor: 'text-amber-600',
      title: 'Importante',
      titleColor: 'text-amber-800 dark:text-amber-300',
      textColor: 'text-amber-700 dark:text-amber-400',
    },
  }[type];

  const Icon = config.icon;

  return (
    <div className={`my-4 flex items-start gap-3 rounded-lg border p-3 ${config.bg}`}>
      <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${config.iconColor}`} />
      <div>
        <p className={`text-xs font-semibold ${config.titleColor}`}>{config.title}</p>
        <p className={`text-sm leading-relaxed ${config.textColor}`}>{text}</p>
      </div>
    </div>
  );
}

/* ─── Paragraph renderer ─────────────────────────────────────── */
function renderParagraph(paragraph: string, pIdx: number) {
  const trimmed = paragraph.trim();

  // Diagram / illustration: [img:diagram-name]
  const imgMatch = trimmed.match(/^\[img:([a-z0-9-]+)\]$/);
  if (imgMatch) {
    const Component = chapterIllustrations[imgMatch[1]];
    if (Component) return <Component key={pIdx} />;
    return null;
  }

  // Callout: [tip]text[/tip], [nota]text[/nota], [importante]text[/importante]
  const calloutMatch = trimmed.match(/^\[(tip|nota|importante)\]([\s\S]*?)\[\/\1\]$/);
  if (calloutMatch) {
    return (
      <Callout
        key={pIdx}
        type={calloutMatch[1] as 'tip' | 'nota' | 'importante'}
        text={calloutMatch[2].trim()}
      />
    );
  }

  // List (starts with -)
  if (trimmed.startsWith('- ')) {
    const items = paragraph.split('\n').filter((line) => line.trim().startsWith('- '));
    return (
      <ul key={pIdx} className="my-3 space-y-1.5 pl-1">
        {items.map((item, iIdx) => (
          <li
            key={iIdx}
            className="flex items-start gap-2 text-sm leading-relaxed text-muted-foreground"
          >
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
            <span>{item.replace(/^-\s*/, '')}</span>
          </li>
        ))}
      </ul>
    );
  }

  // Section heading (ends with :)
  const lines = paragraph.split('\n');
  if (lines.length > 1 && lines[0].trim().endsWith(':') && !lines[0].trim().startsWith('-')) {
    const heading = lines[0].trim();
    const bodyLines = lines.slice(1);
    const hasListItems = bodyLines.some((l) => l.trim().startsWith('-'));

    return (
      <div key={pIdx} className="my-4">
        <h3 className="mb-2 text-sm font-semibold text-foreground">{heading}</h3>
        {hasListItems ? (
          <ul className="space-y-1.5 pl-1">
            {bodyLines
              .filter((l) => l.trim().startsWith('-'))
              .map((item, iIdx) => (
                <li
                  key={iIdx}
                  className="flex items-start gap-2 text-sm leading-relaxed text-muted-foreground"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                  <span>{item.replace(/^-\s*/, '')}</span>
                </li>
              ))}
          </ul>
        ) : (
          <p className="text-sm leading-relaxed text-muted-foreground">
            {bodyLines.join(' ').trim()}
          </p>
        )}
      </div>
    );
  }

  // Numbered steps
  if (/^\d+\.\s/.test(trimmed)) {
    const steps = paragraph.split('\n').filter((line) => /^\d+\.\s/.test(line.trim()));
    return (
      <ol key={pIdx} className="my-3 space-y-1.5 pl-1">
        {steps.map((step, sIdx) => (
          <li
            key={sIdx}
            className="flex items-start gap-2 text-sm leading-relaxed text-muted-foreground"
          >
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
              {sIdx + 1}
            </span>
            <span>{step.replace(/^\d+\.\s*/, '')}</span>
          </li>
        ))}
      </ol>
    );
  }

  // Default: regular paragraph
  return (
    <p key={pIdx} className="my-3 text-sm leading-relaxed text-muted-foreground">
      {paragraph}
    </p>
  );
}

/* ─── Component ──────────────────────────────────────────────── */
export function ManualChapterViewer() {
  const allChapters = manualSections.flatMap((s) => s.chapters);
  const [activeChapterId, setActiveChapterId] = useState(allChapters[0].id);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const activeChapter = allChapters.find((c) => c.id === activeChapterId) ?? allChapters[0];

  function handleSelectChapter(id: string) {
    setActiveChapterId(id);
    setMobileDropdownOpen(false);
    requestAnimationFrame(() => {
      contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  return (
    <div className="flex gap-6">
      {/* Sidebar — desktop only */}
      <aside className="hidden w-64 shrink-0 lg:block">
        <Card>
          <CardContent className="p-3">
            <ChapterSidebar
              sections={manualSections}
              activeId={activeChapterId}
              onSelect={handleSelectChapter}
            />
          </CardContent>
        </Card>
      </aside>

      {/* Main content area */}
      <div className="min-w-0 flex-1">
        {/* Mobile chapter selector */}
        <div className="mb-4 lg:hidden">
          <button
            onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
            className="flex w-full items-center justify-between rounded-lg border bg-background px-4 py-3 text-sm font-medium shadow-sm"
          >
            <span className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              <span>
                Cap. {activeChapter.number}: {activeChapter.title}
              </span>
            </span>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                mobileDropdownOpen && 'rotate-180'
              )}
            />
          </button>

          {/* Mobile dropdown list */}
          <div
            className={cn(
              'mt-1 overflow-hidden rounded-lg border bg-background shadow-lg transition-all duration-200',
              mobileDropdownOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0 border-0'
            )}
          >
            <div className="p-2 max-h-[350px] overflow-y-auto">
              {manualSections.map((section) => (
                <div key={section.id}>
                  <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {section.title}
                  </p>
                  {section.chapters.map((chapter) => (
                    <button
                      key={chapter.id}
                      onClick={() => handleSelectChapter(chapter.id)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
                        'hover:bg-muted/80',
                        activeChapterId === chapter.id
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-muted-foreground'
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                          activeChapterId === chapter.id
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        )}
                      >
                        {chapter.number}
                      </span>
                      {chapter.title}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Chapter content */}
        <div ref={contentRef} />
        <FadeIn key={activeChapterId} direction="up" duration={0.3}>
          <Card>
            <CardContent className="p-6 sm:p-8">
              <div className="mb-6 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-lg font-bold text-primary-foreground">
                  {activeChapter.number}
                </span>
                <h2 className="text-xl font-bold sm:text-2xl">{activeChapter.title}</h2>
              </div>

              <div className="prose prose-sm max-w-none dark:prose-invert">
                {activeChapter.content.split('\n\n').map(renderParagraph)}
              </div>
            </CardContent>
          </Card>
        </FadeIn>
      </div>
    </div>
  );
}
